(() => {
  if (customElements.get('meltonia-serial-search')) return;

  const MAX_PAGES = 10;
  const MAX_RESULTS = 24;
  const REQUEST_TIMEOUT_MS = 6000;

  function parseQuery(value) {
    const match = String(value).trim().match(/^(?:(\d{1,5})\s*)?\/\s*(\d{1,5})$/);
    if (!match) return null;
    const numerator = match[1] === undefined ? null : Number(match[1]);
    const denominator = Number(match[2]);
    if (denominator < 1 || (numerator !== null && (numerator < 1 || numerator > denominator))) return null;
    return { numerator, denominator };
  }

  function matchingSerial(title, query) {
    // A slash is required. Do not combine a grade, card number or year with
    // another number elsewhere in the title, as native search can do.
    const text = String(title);
    const tokens = text.matchAll(/(^|[^\p{L}\p{N}/.,+\-−])(?:(\d{1,5}))?\/(\d{1,5})(?![\p{L}\p{N}/.])/gu);
    for (const token of tokens) {
      const start = token.index + token[1].length;
      const before = text.slice(Math.max(0, start - 60), start);
      const after = text.slice(token.index + token[0].length, token.index + token[0].length + 40);
      // A title's grade/condition/date fraction is not a claimed printed serial.
      // Keep this title-only discovery scope conservative; images require review.
      const explicitSerial = /\b(?:serial(?: number)?|numbered|s\/n|sn)\s*[:#-]?\s*$/i.test(before);
      if (!explicitSerial && (/\b(?:PSA|BGS|SGC|CGC|CSG|TAG|BVG|grade|graded|condition|rating|cert(?:ificate)?|card|centering|surface|corners|edges|mint|date|dated|issued|listed|sold|release|released)\b[^0-9/]{0,24}$/i.test(before) ||
          /^\s*[([]?\s*(?:condition|rating|grade|graded|centering|surface|corners|edges|mint|gem[ -]*mint)\b/i.test(after) ||
          /^\s*[([]?\s*(?:PSA|BGS|SGC|CGC|CSG|TAG|BVG)\b(?!\s+(?:[0-9]|auth))/i.test(after) ||
          (token[2] === undefined && /#[0-9]{1,5}\s+$/.test(before)) ||
          /\b\d{4}-\s*$/.test(before))) continue;
      const numerator = token[2] === undefined ? null : Number(token[2]);
      const denominator = Number(token[3]);
      if (denominator !== query.denominator) continue;
      if (numerator !== null && (numerator < 1 || numerator > denominator)) continue;
      if (query.numerator !== null && numerator !== query.numerator) continue;
      return `${token[2] || ''}/${token[3]}`;
    }
    return null;
  }

  function productUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return null;
      return url;
    } catch {
      return null;
    }
  }

  function imageUrl(value, shopDomain) {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value.startsWith('//') ? `https:${value}` : value, window.location.origin);
      if (url.origin === window.location.origin && /^\/cdn\//.test(url.pathname)) return url;
      if (url.protocol === 'https:' && url.hostname === 'cdn.shopify.com') return url;
      if (url.protocol === 'https:' && url.hostname === shopDomain && /^\/cdn\/shop\//.test(url.pathname)) return url;
      return null;
    } catch {
      return null;
    }
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  class MeltoniaSerialSearch extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.form = this.querySelector('[data-serial-form]');
      this.input = this.querySelector('input[name="serial"]');
      this.status = this.querySelector('[data-serial-status]');
      this.results = this.querySelector('[data-serial-results]');
      this.more = this.querySelector('[data-serial-more]');
      this.submit = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.submit.textContent;
      this.sequence = 0;
      this.submitHandler = (event) => {
        event.preventDefault();
        this.search();
      };
      this.inputHandler = () => {
        this.input.removeAttribute('aria-invalid');
        if (this.controller || this.results.childElementCount || this.status.textContent) {
          this.sequence += 1;
          this.controller?.abort();
          this.controller = null;
          this.results.replaceChildren();
          this.matches = [];
          this.more.hidden = true;
          this.setBusy(false);
          this.status.textContent = 'Search updated. Select Find cards to search again.';
        }
      };
      this.exampleHandler = (event) => {
        const button = event.target.closest('[data-serial-example]');
        if (!button || !this.contains(button)) return;
        this.input.value = button.dataset.serialExample;
        this.inputHandler();
        this.input.focus();
      };
      this.form.addEventListener('submit', this.submitHandler);
      this.input.addEventListener('input', this.inputHandler);
      this.form.addEventListener('click', this.exampleHandler);
      this.moreHandler = () => this.showMore(true);
      this.more.addEventListener('click', this.moreHandler);
      this.form.hidden = false;
    }

    disconnectedCallback() {
      this.sequence += 1;
      this.controller?.abort();
      this.controller = null;
      this.setBusy(false);
      this.form?.removeEventListener('submit', this.submitHandler);
      this.input?.removeEventListener('input', this.inputHandler);
      this.form?.removeEventListener('click', this.exampleHandler);
      this.more?.removeEventListener('click', this.moreHandler);
      this.connected = false;
    }

    setBusy(busy) {
      this.results.setAttribute('aria-busy', String(busy));
      this.submit.disabled = busy;
      this.submit.textContent = busy ? 'Searching…' : this.submitLabel;
    }

    async fetchPage(query, page, signal) {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid search URL.');
      url.search = new URLSearchParams({
        q: `title:"/${query.denominator}"`,
        type: 'product',
        view: 'serial-data',
        'options[prefix]': 'none',
        page: String(page),
      }).toString();
      const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable.');
      const data = await response.json();
      if (!Array.isArray(data.products) || data.products.length > 50 || !Number.isInteger(data.page) || data.page !== page || !Number.isInteger(data.pages) || data.pages < 0) {
        throw new Error('Invalid search response.');
      }
      return data;
    }

    async search() {
      const sequence = ++this.sequence;
      this.controller?.abort();
      this.controller = null;
      const query = parseQuery(this.input.value);
      this.results.replaceChildren();
      this.more.hidden = true;
      this.setBusy(false);
      if (!query) {
        this.input.setAttribute('aria-invalid', 'true');
        this.status.textContent = 'Enter a full serial such as 03/25 or a print run such as /99. The first number must be between 1 and the print run.';
        this.input.focus();
        return;
      }
      this.input.removeAttribute('aria-invalid');
      const controller = new AbortController();
      this.controller = controller;
      this.setBusy(true);
      this.status.textContent = 'Searching available listings…';
      const matches = [];
      const seen = new Set();
      let totalPages = 1;
      let timeout;
      try {
        for (let page = 1; page <= Math.min(totalPages, MAX_PAGES); page += 1) {
          timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
          const data = await this.fetchPage(query, page, controller.signal);
          clearTimeout(timeout);
          if (sequence !== this.sequence) return;
          totalPages = data.pages;
          for (const product of data.products) {
            if (product.available !== true || typeof product.title !== 'string' || typeof product.price !== 'string') continue;
            const serial = matchingSerial(product.title, query);
            const url = productUrl(product.url);
            const image = imageUrl(product.image, this.dataset.shopDomain);
            if (!serial || !url || !image || seen.has(url.pathname)) continue;
            seen.add(url.pathname);
            matches.push({ ...product, url, image, serial });
          }
        }
        if (sequence !== this.sequence) return;
        const partial = totalPages > MAX_PAGES;
        if (matches.length === 0) {
          this.status.textContent = partial
            ? 'No matches in the first 500 candidate listings. This search is partial; more listings may exist beyond this limit.'
            : 'No available cards with photos match that number in their listing titles. Try another serial or print run.';
        } else {
          this.matches = matches;
          this.partial = partial;
          this.shown = 0;
          this.showMore(false);
        }
      } catch {
        if (sequence !== this.sequence) return;
        this.status.textContent = 'Search could not finish. Select Find cards to try again.';
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) {
          this.controller = null;
          this.setBusy(false);
        }
      }
    }

    showMore(moveFocus) {
      const fragment = document.createDocumentFragment();
      const nextMatches = this.matches.slice(this.shown, this.shown + MAX_RESULTS);
      nextMatches.forEach((product) => fragment.append(this.productCard(product)));
      const firstLink = fragment.querySelector('a');
      this.results.append(fragment);
      this.shown += nextMatches.length;
      this.more.hidden = this.shown >= this.matches.length;
      const count = this.matches.length;
      const summary = this.shown < count
        ? `Showing ${this.shown} of ${count} matching available listings.`
        : `${count} matching available ${count === 1 ? 'listing' : 'listings'}.`;
      this.status.textContent = summary + (this.partial ? ' Partial search: only the first 500 candidate listings were checked.' : '');
      if (moveFocus) firstLink?.focus();
    }

    productCard(product) {
      const card = element('article', 'm-city-product-card');
      const link = element('a', 'm-city-product-card__link');
      link.href = product.url.href;
      const media = element('div', 'm-city-product-card__media');
      const image = element('img');
      image.src = product.image.href;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      if (Number.isFinite(product.image_width) && product.image_width > 0) image.width = product.image_width;
      if (Number.isFinite(product.image_height) && product.image_height > 0) image.height = product.image_height;
      media.append(image);
      const body = element('div', 'm-city-product-card__body');
      body.append(
        element('h3', '', product.title),
        element('p', 'price price-item m-city-product-card__price', product.price),
        element('p', 'm-city-serial__serial', `As listed: ${product.serial}`),
        element('span', 'm-city-card-action', 'View card')
      );
      link.append(media, body);
      card.append(link);
      return card;
    }
  }

  customElements.define('meltonia-serial-search', MeltoniaSerialSearch);
})();
